
import { useEffect } from 'react';

interface DynamicTitleProps {
  title?: string;
  suffix?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
}

export default function DynamicTitle({ 
  title, 
  suffix = "Documenting the AI Solopreneur Journey",
  description,
  keywords,
  canonicalUrl
}: DynamicTitleProps) {
  useEffect(() => {
    // Set the base title
    const baseTitle = title ? `${title} - Aram AI` : `Aram AI - ${suffix}`;
    
    // Update the document title
    document.title = baseTitle;
    
    // Helper function to update or create meta tag
    const updateMetaTag = (selector: string, attribute: string, content: string) => {
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        if (attribute === 'property') {
          meta.setAttribute('property', selector.replace('[property="', '').replace('"]', ''));
        } else {
          meta.setAttribute('name', selector.replace('[name="', '').replace('"]', ''));
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Update title meta tags
    updateMetaTag('[property="og:title"]', 'property', baseTitle);
    updateMetaTag('[name="twitter:title"]', 'name', baseTitle);
    
    // Update description if provided
    if (description) {
      updateMetaTag('[name="description"]', 'name', description);
      updateMetaTag('[property="og:description"]', 'property', description);
      updateMetaTag('[name="twitter:description"]', 'name', description);
    }
    
    // Update keywords if provided
    if (keywords) {
      updateMetaTag('[name="keywords"]', 'name', keywords);
    }
    
    // Update canonical URL if provided
    if (canonicalUrl) {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalUrl);
      
      updateMetaTag('[property="og:url"]', 'property', canonicalUrl);
    }
    
    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = "Aram AI - Documenting the AI Solopreneur Journey";
    };
  }, [title, suffix, description, keywords, canonicalUrl]);

  // This component doesn't render anything visible
  return null;
}
